import { useState } from "react";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";

import { MUTATE_REACTION } from "../../../GraphQL/addReaction";
import { BASE_REQUEST, MONTAGE_REQUEST } from "../../../GraphQL/clipFeed";
import { DELETE_MONTAGE } from "../../../GraphQL/montage";
import { EClipsViews } from "../../../@types";

export interface IClipFeedQuery {
  variables: {
    search?: string;
    filter?: string[];
    page?: number;
    game?: number | string | null;
    remixes?: boolean;
    user?: string;
    followers?: boolean;
    mobile?: boolean;
    hlsOnly?: boolean;
    limit?: number;
  };
}

export interface IRefetchQuery {
  page?: number;
  search?: string;
  filter?: string[];
  game?: number | string | null;
  remixes?: boolean;
  user?: string;
  followers?: boolean;
  mobile?: boolean;
  hlsOnly?: boolean;
  limit?: number;
}

export const useProfileFeed = (view: EClipsViews) => {
  const [error, setError] = useState<ApolloError | null>(null);
  const isMontage = view === EClipsViews.Montages;

  const [mutateReaction] = useMutation(MUTATE_REACTION);
  const [deleteMontage] = useMutation(DELETE_MONTAGE);

  const [requestClipsFeed, { refetch, loading, data, error: requestError }] =
    useLazyQuery(BASE_REQUEST, {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  const [requestMontageFeed, { data: montageData, loading: montagesLoading }] =
    useLazyQuery(MONTAGE_REQUEST, {
      fetchPolicy: "cache-and-network",
      onError(error) {
        setError(error);
      },
    });

  const fetchData = (query: IClipFeedQuery, userId: string) => {
    if (isMontage) {
      return requestMontageFeed({
        variables: {
          user: userId,
          search: "createdDate",
          page: query.variables.page,
        },
      });
    } else {
      return requestClipsFeed(query);
    }
  };

  const refetchData = (query: IRefetchQuery, userId: string) => {
    if (isMontage) {
      return requestMontageFeed({
        variables: {
          search: "createdDate",
          page: query.page,
          user: userId,
        },
      });
    } else {
      return refetch(query);
    }
  };

  const deleteUserMontage = async (montageId: string) => {
    try {
      await deleteMontage({
        variables: { montageId },
      });
      return true;
    } catch (err) {
      setError(err as ApolloError);
      return false;
    }
  };

  const reactToContent = async (clipId: string, reaction: string) => {
    try {
      await mutateReaction({
        variables: { clipId, reaction },
      });
      return true;
    } catch (err) {
      setError(err as ApolloError);
      return false;
    }
  };

  if (requestError && !error) setError(requestError);

  return {
    loading: loading || montagesLoading,
    montagesLoading,
    data: data?.getClip ?? montageData?.montages,
    error,
    fetchData,
    refetchData,
    deleteUserMontage,
    reactToContent,
    mutateReaction,
    deleteMontage,
  };
};
